import { Controller } from '@hotwired/stimulus';

// Scroll positions of a board taken right before Turbo replaced its page, by board id, until the new page takes them.
const pending = new Map();

/**
 * Board scroll - leaving a card goes back to the board with a page visit (close, Escape, backdrop, browser back,
 * archive), which replaces the board and starts every scroll area at the top. Right before Turbo renders a page
 * that shows this board again, this remembers how far the board was scrolled sideways and each list down (by list
 * id), and the new page puts them back. Turbo may first render a cached preview: it gets the positions too, and the
 * real page after it.
 * Targets: lists - the sideways scrolling lists | cards - each list's scrolling cards, carrying data-list-id
 * Values: board (String) - id of the board
 * @example
 * <div data-controller="board-scroll" data-board-scroll-board-value="9b1c…">
 *   <div data-board-scroll-target="lists">
 *     <div data-board-scroll-target="cards" data-list-id="4f2a…">…</div>
 *   </div>
 * </div>
 */
export default class extends Controller {
  static targets = ['lists', 'cards'];
  static values = { board: String };

  initialize () {
    this.remember = this.remember.bind(this);
    this.restoreAfterVisit = this.restoreAfterVisit.bind(this);
  }

  connect () {
    // Turbo marks the document as a preview before it renders one, and clears it before the real page
    this.preview = document.documentElement.hasAttribute('data-turbo-preview');
    document.addEventListener('turbo:before-render', this.remember);

    this.saved = pending.get(this.boardValue);
    if (!this.saved) return;

    if (!this.preview) pending.delete(this.boardValue);
    this.restore();
    // Turbo scrolls the window to the top after it renders a visit
    document.addEventListener('turbo:load', this.restoreAfterVisit, { once: true });
  }

  disconnect () {
    document.removeEventListener('turbo:before-render', this.remember);
    document.removeEventListener('turbo:load', this.restoreAfterVisit);
  }

  // turbo:before-render: only when the new page shows this board again and replaces it (a morph keeps the elements,
  // and their scroll with them). A preview keeps what the page before it saved.
  remember (event) {
    if (event.detail.renderMethod === 'morph' || this.preview) return;

    const next = event.detail.newBody?.querySelector(`[data-controller~="board-scroll"][data-board-scroll-board-value="${this.boardValue}"]`);
    if (!next) return;

    pending.set(this.boardValue, {
      top: window.scrollY,
      left: this.hasListsTarget ? this.listsTarget.scrollLeft : 0,
      lists: Object.fromEntries(this.cardsTargets.map((cards) => [cards.dataset.listId, cards.scrollTop]))
    });
  }

  restore () {
    const { top, left, lists } = this.saved;
    window.scrollTo(0, top);
    if (this.hasListsTarget) this.listsTarget.scrollLeft = left;
    this.cardsTargets.forEach((cards) => {
      if (cards.dataset.listId in lists) cards.scrollTop = lists[cards.dataset.listId];
    });
  }

  restoreAfterVisit () {
    if (this.saved) this.restore();
  }
}
